// =============================================================================
// requests/kpiRequests.js
//
// HTTP calls to the KPI API. Convention: same as trackerRequests.js / weeklyUpdateRequests.js.
//   - Each function makes ONE call to ONE endpoint
//   - They return the Axios Promise
//   - No function touches state or dispatch
// =============================================================================
import config from '../../../configuration/configuration.js';
import AxiosRequest from '../../../utils/AxiosRequest.js';

const BASE = config.API_URL + 'kpi';

// =============================================================================
// Incidents
// =============================================================================

// Returns a PagedResultDto ({ items, totalCount, page, pageSize }).
// `params` may include: status, tech, kpiName, vendor, severity, siteId,
// cellId, durationBucket, page, pageSize — every filter dimension is now
// applied server-side, plus real pagination (see KpiController.GetIncidents
// / KpiRepository.GetIncidentsAsync). Axios drops undefined keys from the
// query string automatically, so callers can just omit whatever isn't
// currently active.
export function apiFetchIncidents(params) {
	return AxiosRequest({
		method: 'GET',
		url:    BASE + '/incidents',
		params: params,
	});
}

export function apiFetchIncidentHistory(incidentId) {
	return AxiosRequest({
		method: 'GET',
		url:    BASE + '/incidents/' + incidentId + '/history',
	});
}

// =============================================================================
// Lookups (vendors, techs, kpis, severities, statuses from the DB)
// =============================================================================

export function apiFetchLookups() {
	return AxiosRequest({ method: 'GET', url: BASE + '/lookups' });
}

// =============================================================================
// KPI Rules
// =============================================================================

export function apiFetchRules() {
	return AxiosRequest({ method: 'GET', url: BASE + '/rules' });
}

export function apiFetchRuleById(id) {
	return AxiosRequest({ method: 'GET', url: BASE + '/rules/' + id });
}

export function apiCreateRule(rule) {
	return AxiosRequest({ method: 'POST', url: BASE + '/rules', data: rule });
}

export function apiUpdateRule(id, rule) {
	return AxiosRequest({ method: 'PUT', url: BASE + '/rules/' + id, data: rule });
}

export function apiDeleteRule(id) {
	return AxiosRequest({ method: 'DELETE', url: BASE + '/rules/' + id });
}

// =============================================================================
// Formula validation
// =============================================================================

export function apiValidateFormula(payload) {
	return AxiosRequest({
		method: 'POST',
		url:    BASE + '/rules/validate',
		data:   payload,
	});
}

// =============================================================================
// Engine
// =============================================================================

export function apiFetchEngineLogs() {
	return AxiosRequest({ method: 'GET', url: BASE + '/engine/logs' });
}

export function apiRunBaseline(date) {
	return AxiosRequest({
		method: 'POST',
		url:    BASE + '/engine/baseline',
		params: { date: date },
	});
}

export function apiRunDetection(date) {
	return AxiosRequest({
		method: 'POST',
		url:    BASE + '/engine/detection',
		params: { date: date },
	});
}

export function apiRunFullPipeline(date) {
	return AxiosRequest({
		method: 'POST',
		url:    BASE + '/engine/run',
		params: { date: date },
	});
}

// =============================================================================
// Sites with coordinates (feeds the map)
// =============================================================================
export function apiFetchSites() {
	return AxiosRequest({ method: 'GET', url: BASE + '/sites' });
}